//@ts-nocheck
import React, { useState, useEffect, useMemo } from 'react';
import moment from 'moment';
import {
  Table,
  DatePicker,
  EurIcon,
  UsdIcon,
  Select,
  RangePicker,
  DatePickerProps,
  Input,
  ArrowTransferOutIcon,
  Modal,
  InputAmount,
  notification,
} from '@gamesb42/ui-kit';
import { useHistory } from 'react-router-dom';
import queryString from 'query-string';
import { Form, FormInstance } from 'antd';

import { formatAmount, getYYYYMMDD, getNameCurrencyOfCode } from 'helpers/formatters';
import ContentWrapper from 'components/ContentWrapper';
import baseRequest from 'queries/baseRequest';
import getTransactionErrorMessage from 'helpers/getTransactionErrorMessage';
import { CURRENCY_OPTIONS } from 'constants/currency';
import CompanySelect from 'components/CompanySelect';
import Flex from 'components/Flex';
import useCompany, { useCompanyType } from 'hooks/api/useCompany';

import s from './repaymentSchedule.module.scss';

interface QueryParams {
  sortedBy?: string;
}

export default function RepaymentSchedule() {
  const [repaymentLoading, setRepaymentLoading] = useState(true);
  const [repaymentData, setRepaymentData] = useState([]);
  const [total, setTotal] = useState({ eur: '', usd: '' });
  const history = useHistory();
  const query: QueryParams = queryString.parse(history.location.search, { parseNumbers: true });
  const [currency, setCurrency] = useState(query?.currency);
  const [period, setPeriod] = useState<DatePickerProps['value']>(undefined);
  const [productType, setProductType] = useState();
  const [sender, setSender] = useState();
  const [clientId, setClientId] = useState();
  const [open, setOpen] = useState();
  const [activeRecord, setActiveRecord] = useState();
  const [logo, setLogo] = useState();
  const [isCreatingTransaction, setIsCreatingTransaction] = useState(false);
  const [form] = Form.useForm();

  const { getAccountsByIdCompany, accountsByIdCompany }: useCompanyType = useCompany();

  const formDate = Form.useWatch('date', form);
  const formAmount = Form.useWatch('amount', form);

  const activeRecordAvailableBalance = useMemo(() => {
    if (!activeRecord || !accountsByIdCompany) return 0;
    const recordCurrency = activeRecord.amount_usd ? 'USD' : 'EUR';
    return (
      accountsByIdCompany.find((item) => {
        return (
          getNameCurrencyOfCode(item.currency) === recordCurrency &&
          item.type === 'counterparty-client' &&
          item.company_id.replaceAll('-', '') === activeRecord.company_id.replaceAll('-', '')
        );
      })?.balance || 0
    );
  }, [activeRecord, accountsByIdCompany]);

  const isFormValid = useMemo(() => {
    if (!formDate || !formAmount) return false;
    if (Number(formAmount) > activeRecordAvailableBalance) return false;
    return true;
  }, [formDate, formAmount, activeRecordAvailableBalance]);

  const repaymentColumns = [
    {
      title: 'Sender',
      dataIndex: 'sender',
      minWidth: '116px',
      align: 'left' as 'left',
    },
    {
      title: 'Client',
      dataIndex: 'client',
      minWidth: '116px',
      align: 'left' as 'left',
    },
    {
      title: <div className={s.header}>Product type</div>,
      dataIndex: 'type_of_product',
      minWidth: '120px',
      align: 'left' as 'left',
    },
    {
      title: <div className={s.header}>Expected date'</div>,
      minWidth: '128px',
      align: 'left' as 'left',
      render: (_: any, record: any) => {
        return moment(record.expected_date, 'YYYY-MM-DD').format('DD.MM.YYYY');
      },
    },
    {
      title: <div className={s.header}>Payment type</div>,
      dataIndex: 'type_of_payment',
      minWidth: '124px',
      align: 'left' as 'left',
      render: (_: any, record: any) => {
        return record.type_of_payment === 'Penalty' ? 'DI' : record.type_of_payment;
      },
    },
    {
      title: 'EUR',
      width: '116px',
      align: 'right' as 'right',
      render: (_: any, record: any) => {
        return formatAmount(record.amount_eur);
      },
    },
    {
      title: 'USD',
      width: '116px',
      align: 'right' as 'right',
      render: (_: any, record: any) => {
        return formatAmount(record.amount_usd);
      },
    },
    {
      title: 'Details',
      dataIndex: 'details',
      width: '150px',
      align: 'left' as 'left',
      render: (_: any, record: any) => {
        return (
          <Flex gap="16px" justify="space-between">
            <div className={s.link} onClick={() => history.push(`${record.url}`)}>
              {record.details}
            </div>
            <div
              style={{
                cursor: 'pointer',
              }}
              onClick={() => {
                setActiveRecord(record);
                setOpen(true);
              }}
            >
              <ArrowTransferOutIcon size={24} />
            </div>
          </Flex>
        );
      },
    },
  ];

  const modalColumns = [
    {
      title: ['NOW', 'FEATAURE'].includes(activeRecord?.type_of_product) ? 'Revenue Source' : 'AO',
      // dataIndex: 'details',
      minWidth: '88px',
      align: 'left' as 'left',
      render: (_: any, record: any) => {
        if (['NOW', 'FEATAURE'].includes(activeRecord?.type_of_product)) {
          return (
            <Flex gap="4px" align="center">
              <img src={logo} alt="" style={{ width: 24 }} />
              {record?.sender}
            </Flex>
          );
        } else {
          return record?.details;
        }
      },
    },
    {
      title: 'Type',
      dataIndex: 'type_of_payment',
      minWidth: '88px',
      align: 'left' as 'left',
    },
    {
      title: 'Outstanding',
      minWidth: '88px',
      align: 'left' as 'left',
      render: (_: any, record: any) => {
        if (record.amount_eur) {
          return <div>€{formatAmount(Math.abs(record.amount_eur))}</div>;
        }

        if (record.amount_usd) {
          return <div>${formatAmount(Math.abs(record.amount_usd))}</div>;
        }

        return <div></div>;
      },
    },
    {
      title: 'Available Balance',
      minWidth: '88px',
      align: 'left' as 'left',
      render: () => {
        const symbol = activeRecord?.amount_usd ? '$' : '€';
        return `${symbol}${activeRecordAvailableBalance}`;
      },
    },
  ];

  async function getRepaymentSchedule() {
    try {
      let params = {};

      if (currency) {
        params.currency = currency.toLowerCase();
      }

      if (period) {
        params.from_date = period[0].format('YYYY-MM-DD');
        params.to_date = period[1].format('YYYY-MM-DD');
      }

      if (productType) {
        params.product_type = productType;
      }

      if (sender) {
        params.sender = sender;
      }

      if (clientId) {
        params.company_id = clientId;
      }

      const result = await baseRequest({
        path: `${process.env.REACT_APP_BASE_URL}/reports/repayment_schedule?${queryString.stringify(params)}`,
        options: {
          method: 'GET',
        },
      });

      setRepaymentData(result.items);
      setTotal({
        eur: result.items.reduce((acc, curr) => acc + (curr.amount_eur || 0), 0),
        usd: result.items.reduce((acc, curr) => acc + (curr.amount_usd || 0), 0),
      });
    } catch (error) {
      console.error('Ошибка при получении графика погашений:', error);
    } finally {
      setRepaymentLoading(false);
    }
  }

  async function createTransaction() {
    if (isCreatingTransaction) return;

    // InputAmount синхронизирует значение с формой только по blur
    (document.activeElement as HTMLElement | null)?.blur();

    let result;

    try {
      result = await form.validateFields();
    } catch {
      return;
    }

    setIsCreatingTransaction(true);

    try {
      if (activeRecord?.type_of_payment === 'Purchased Receivables') {
        await baseRequest({
          path: `${process.env.REACT_APP_BASE_URL}/transaction/create_transaction_in_to_claim`,
          suppressErrorNotificationForStatuses: [400, 422],
          options: {
            method: 'POST',
            body: JSON.stringify({
              date: result.date.format('YYYY-MM-DD'),
              purchaseRequestClaimId: activeRecord.claim_id,
              amount: result.amount,
            }),
          },
        });
      }

      if (['Penalty', 'Principal', 'Commission'].includes(activeRecord?.type_of_payment)) {
        const categoryMap = {
          Penalty: 'internal-default-interest',
          Principal: 'internal-principal-amount',
          Commission: 'internal-commission',
        };

        await baseRequest({
          path: `${process.env.REACT_APP_BASE_URL}/transaction/`,
          suppressErrorNotificationForStatuses: [400, 422],
          options: {
            method: 'POST',
            body: JSON.stringify({
              type: 'internal',
              value_datetime: result.date.toISOString(),
              factoring_order_id: activeRecord.order_id,
              factoring_order_number: activeRecord.order_number,
              category: categoryMap[activeRecord?.type_of_payment],
              factoring_company_id: activeRecord.company_id,
              factoring_company_name: activeRecord.client,
              amount: {
                currency: activeRecord.amount_eur ? '978' : '840',
                amount: result.amount,
              },
            }),
          },
        });
      }

      notification.success({
        theme: 'dark',
        message: 'Transaction created successfully',
      });

      setOpen(false);
      form.resetFields();
      getRepaymentSchedule();
    } catch (e) {
      notification.error({
        theme: 'dark',
        message: getTransactionErrorMessage(
          { status: (e as { status?: number })?.status, text: (e as Error)?.message },
          'Failed to create transaction',
        ),
      });
    } finally {
      setIsCreatingTransaction(false);
    }
  }

  useEffect(() => {
    getRepaymentSchedule();
  }, [currency, period, productType, sender, clientId]);

  useEffect(() => {
    if (activeRecord) {
      const date = new Date();
      date.setDate(date.getDate() + 1);
      const balanceDate = getYYYYMMDD(date);

      getAccountsByIdCompany({ company_id: activeRecord?.company_id, balanceDate });

      if (['NOW', 'FEATAURE'].includes(activeRecord?.type_of_product)) {
        // get logo

        fetch(`${process.env.REACT_APP_PDS_URL}/v1/stores/find-by-code/${activeRecord?.sender.replaceAll(' ', '')}`, {
          method: 'GET',
        })
          .then((response) => response.json())
          .then((result) => setLogo(result.logo))
          .catch((error) => console.error(error));
      }
    }
  }, [activeRecord]);

  return (
    <>
      <ContentWrapper style={{ padding: '20px 0 0 0', position: 'relative' }} maxWidthContent fullHeight>
        <div className={s.contentWrapper}>
          <div className={s.center}>
            <div className={s.title}>Repayment Schedule</div>

            <div className={s.filters}>
              <Select
                value={currency}
                style={{ width: '112px' }}
                label={'Currency'}
                options={CURRENCY_OPTIONS}
                onChange={(e) => setCurrency(e)}
                allowClear
              />
              <RangePicker size="medium" style={{ width: '260px' }} value={period} onChange={setPeriod} />
              <Input
                value={sender}
                onChange={(e) => setSender(e.target.value)}
                style={{ width: '266px' }}
                label="Sender"
                placeholder="Sender"
              />
              <div>
                <CompanySelect
                  label={'Client'}
                  size="middle"
                  width="266px"
                  value={clientId || undefined}
                  onChange={(id) => setClientId(id)}
                  allowClear
                />
              </div>
              <Select
                value={productType}
                style={{ width: '260px' }}
                label={'Product type'}
                options={[
                  { label: 'MAX', value: 'MAX' },
                  { label: 'NOW', value: 'NOW' },
                  { label: 'Future', value: 'Future' },
                ]}
                onChange={(e) => setProductType(e)}
                allowClear
              />
            </div>

            <Table
              loading={repaymentLoading}
              initialHeight="500px"
              className={s.table}
              columns={repaymentColumns}
              dataSource={repaymentData}
              footer={() => (
                <div
                  style={{ width: '100%', display: 'flex', justifyContent: 'space-between', padding: '0 150px 0 0' }}
                >
                  <div style={{ paddingLeft: '16px' }}>
                    <b>Total</b>
                  </div>

                  <div style={{ display: 'flex' }}>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', width: '120px', paddingRight: '12px' }}>
                      <b>{formatAmount(total.eur)}</b>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', width: '120px', paddingRight: '15px' }}>
                      <b>{formatAmount(total.usd)}</b>
                    </div>
                  </div>
                </div>
              )}
            />
          </div>
        </div>
      </ContentWrapper>
      <Modal
        open={open}
        title="Create transaction"
        onOk={createTransaction}
        confirmLoading={isCreatingTransaction}
        okButtonProps={{
          onMouseDown: () => {},
          disabled: !isFormValid || isCreatingTransaction,
          style: !isFormValid ? { backgroundColor: '#d9d9d9', borderColor: '#d9d9d9', color: '#fff', opacity: 1 } : {},
        }}
        okText="Create transaction"
        cancelText="Cancel"
        onCancel={() => {
          setOpen(false);
          form.resetFields();
        }}
        width="766px"
        centered
      >
        <Table columns={modalColumns} dataSource={[activeRecord]} />
        <Form form={form}>
          <Flex gap="32px" style={{ marginTop: '32px' }}>
            <div style={{ width: '100%' }}>
              <Form.Item name="date" rules={[{ required: true, message: 'Please select Date!' }]}>
                <DatePicker label="Date" size="large" format="DD.MM.YYYY" aria-required />
              </Form.Item>
            </div>
            <div style={{ width: '100%' }}>
              <Form.Item
                name="amount"
                rules={[
                  { required: true, message: 'Please enter Amount!' },
                  {
                    validator: (_, value) => {
                      if (value && Number(value) > activeRecordAvailableBalance) {
                        return Promise.reject(
                          new Error(
                            `Amount exceeds available balance (${activeRecord?.amount_usd ? '$' : '€'}${formatAmount(
                              activeRecordAvailableBalance,
                            )})`,
                          ),
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <InputAmount
                  label="Amount"
                  size="large"
                  aria-required
                  currency={activeRecord?.amount_eur ? 'EUR' : 'USD'}
                />
              </Form.Item>
            </div>
          </Flex>
        </Form>
      </Modal>
    </>
  );
}
